import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { ArrowLeft, Check, CircleAlert, CircleCheck, Plus, Sparkles, Trash2, Upload, X } from 'lucide-react';
import FileUploadPopup from '../../../components/popup/file-upload-popup';
import { StorefrontService } from '../../../core/services/storefront.service';

import { InstagramDMAutomation } from '../../../core/services/dm-automation-instagram.service';
import { FormValidation } from '../../../core/utility';
import { Toggle } from '../../../modules/shared/components/settings/SettingsParts';
import { StoreField } from './StoreField';

interface Props {
    storefront_id: number;
    product?: any;
    // Whichever CashKaro/Trackier Affiliate Deals campaign is currently linked to this
    // storefront, if any — the "Feature in Affiliate Deals" toggle below only shows when set.
    cashkaroCampaignId?: number | null;
    cashkaroCampaignTitle?: string | null;
    onSaved: () => void;
    onCancel: () => void;
    UserReducer?: any;
}

const EMPTY_FORM = {
    sku: '', product_title: '', product_description: '', product_url: '',
    price: '', discount_price: '', currency: 'INR', category: '',
    commission_percent: '', in_stock: true, image_urls: [''],
    // Defaults to true — a newly added product is featured in the linked campaign
    // unless the brand explicitly opts it out.
    featured_in_campaign: true,
};

// Reuse the Zap Link product-detail fetcher (same API used in the Zap Link and Zap
// Storefront flows) to auto-fill product fields from a pasted product URL.
const zapProductService = new InstagramDMAutomation();
const formValidation = new FormValidation();

// Same slug + random-suffix scheme as the influencer Zap Storefront form, so SKUs
// generated by either surface follow one convention.
const generateSku = (title: string) =>
    `${title.toLowerCase().replace(/[^a-z0-9]/g, '-').slice(0, 24)}-${Math.random().toString(36).slice(2, 7)}`;

const ProductFormComponent: React.FC<Props> = ({ storefront_id, product, cashkaroCampaignId, cashkaroCampaignTitle, onSaved, onCancel, UserReducer }) => {
    const [saving, setSaving] = useState(false);
    const [alert, setAlert] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [uploadIdx, setUploadIdx] = useState<number | null>(null); // which image slot is uploading
    // Auto-fill from product URL (Zap Link fetch)
    const [urlError, setUrlError] = useState('');
    const [fetchingProduct, setFetchingProduct] = useState(false);
    const [fetchStatus, setFetchStatus] = useState<'idle' | 'success' | 'failed'>('idle');
    const [lastFetchedUrl, setLastFetchedUrl] = useState('');

    const uploadPath = `accounts/${UserReducer?.account_id}/brand/${UserReducer?.brand_id}/storefront/products`;

    useEffect(() => {
        if (product) {
            setForm({
                sku: product.sku || '',
                product_title: product.product_title || '',
                product_description: product.product_description || '',
                product_url: product.product_url || '',
                price: product.price || '',
                discount_price: product.discount_price || '',
                currency: product.currency || 'INR',
                category: product.category || '',
                commission_percent: product.commission_percent || '',
                in_stock: product.in_stock !== 0,
                image_urls: product.image_urls?.length ? product.image_urls : [''],
                featured_in_campaign: !!product.is_campaign_featured,
            });
        }
    }, [product]);

    const setImageUrl = (idx: number, val: string) => {
        setForm(f => {
            const urls = [...f.image_urls];
            urls[idx] = val;
            return { ...f, image_urls: urls };
        });
    };

    const addImageUrl = () => {
        if (form.image_urls.length < 5) setForm(f => ({ ...f, image_urls: [...f.image_urls, ''] }));
    };

    const removeImageUrl = (idx: number) => {
        setForm(f => ({ ...f, image_urls: f.image_urls.filter((_, i) => i !== idx) }));
    };

    const fetchProductDetails = async (rawUrl: string) => {
        const url = formValidation.NormalizeAffiliateLink(rawUrl);
        if (!url || url === lastFetchedUrl) return;
        const urlCheck = formValidation.GetAffiliateLinkValidate(url);
        if (!urlCheck.status) {
            setUrlError(urlCheck.message);
            return;
        }
        setUrlError('');
        setForm(f => (f.product_url === url ? f : { ...f, product_url: url }));
        setLastFetchedUrl(url);
        setFetchingProduct(true);
        setFetchStatus('idle');
        try {
            const res: any = await zapProductService.fetchZapLinkProduct({ product_url: url, allow_other_source: true });
            if (res?.status === 1 && res?.data) {
                const d = res.data;
                setForm(f => {
                    const cleanImages = (f.image_urls || []).filter(u => u && u.trim() && u !== d.product_image_url);
                    const title = d.product_name || f.product_title;
                    return {
                        ...f,
                        product_title: title,
                        product_description: d.og_description || f.product_description,
                        price: (d.price !== null && d.price !== undefined && d.price !== '') ? String(d.price) : f.price,
                        currency: d.currency || f.currency,
                        image_urls: d.product_image_url ? [d.product_image_url, ...cleanImages].slice(0, 5) : (f.image_urls?.length ? f.image_urls : ['']),
                        // Pre-fill a SKU from the fetched title so the field isn't left empty —
                        // still a normal editable input, the user can overwrite it before saving.
                        sku: f.sku.trim() || (title ? generateSku(title) : f.sku),
                    };
                });
                setFetchStatus('success');
            } else {
                setFetchStatus('failed');
            }
        } catch {
            setFetchStatus('failed');
        } finally {
            setFetchingProduct(false);
        }
    };

    const handleSubmit = async () => {
        if (!form.sku || !form.product_title || !form.product_url) {
            setAlert({ type: 'error', msg: 'SKU, title, and affiliate URL are required' });
            return;
        }

        const priceCheck = formValidation.GetOptionalPriceValidate(form.price);
        if (!priceCheck.status) {
            setAlert({ type: 'error', msg: priceCheck.message });
            return;
        }

        const imageCheck = formValidation.GetRequiredImageValidate(form.image_urls || []);
        if (!imageCheck.status) {
            setAlert({ type: 'error', msg: imageCheck.message });
            return;
        }

        const discountCheck = formValidation.GetOptionalPriceValidate(form.discount_price);
        if (!discountCheck.status) {
            setAlert({ type: 'error', msg: discountCheck.message });
            return;
        }
        // SKU is optional in the form — auto-generate one from the title if the user left it blank.
        const sku = form.sku.trim() || generateSku(form.product_title);
        setSaving(true);
        setAlert(null);
        try {
            const payload = {
                ...form,
                sku,
                storefront_id,
                product_id: product?.product_id,
                price: form.price !== '' && form.price !== null && form.price !== undefined ? parseFloat(form.price as any) : null,
                discount_price: form.discount_price !== '' && form.discount_price !== null && form.discount_price !== undefined ? parseFloat(form.discount_price as any) : null,
                commission_percent: form.commission_percent ? parseFloat(form.commission_percent as any) : 0,
                in_stock: form.in_stock ? 1 : 0,
                image_urls: form.image_urls.filter(u => u.trim())
            };
            const res = product
                ? await StorefrontService.updateProduct(payload)
                : await StorefrontService.createProduct(payload);

            if (res?.status === 1) {
                setAlert({ type: 'success', msg: product ? 'Product updated!' : 'Product created!' });
                setTimeout(() => onSaved(), 800);
            } else {
                setAlert({ type: 'error', msg: res?.info || 'Failed to save product' });
            }
        } catch {
            setAlert({ type: 'error', msg: 'Something went wrong' });
        } finally {
            setSaving(false);
        }
    };

    const disabled = fetchingProduct;
    const urlHint = fetchingProduct
        ? 'Fetching product details…'
        : fetchStatus === 'success'
            ? <span className="brd-store__ok">Details auto-filled. Review and edit anything below if needed.</span>
            : 'Paste the product URL, then tap Auto-fill to fetch title, price and image.';
    const urlProblem = urlError || (fetchStatus === 'failed' ? "Couldn't fetch details automatically — please fill the fields below manually." : '');

    return (
        <div className="brd-container--narrow">
            <header className="brd-page-head">
                <button type="button" className="brd-icon-btn brd-icon-btn--filled" onClick={onCancel} aria-label="Back to products" disabled={saving}>
                    <ArrowLeft size={20} />
                </button>
                <div className="brd-page-head__text">
                    <h2 className="brd-page-head__title">{product ? 'Edit Product' : 'Add Product'}</h2>
                    <p className="brd-page-head__sub">{product ? product.product_title : 'Paste a product link and we’ll fill in the rest.'}</p>
                </div>
            </header>

            {alert && (
                <div className={`brd-banner ${alert.type === 'error' ? 'brd-banner--error' : 'brd-banner--info'}`} role="status">
                    {alert.type === 'error' ? <CircleAlert size={16} /> : <CircleCheck size={16} />}
                    <span style={{ flex: 1 }}>{alert.msg}</span>
                    <button type="button" onClick={() => setAlert(null)} aria-label="Dismiss"><X size={14} /></button>
                </div>
            )}

            <section className="brd-section">
                <h3 className="brd-section__title">Product link</h3>
                {/* Product URL — first field. Paste it and tap Auto-fill to fetch the details. */}
                <StoreField label="Product URL" required hint={urlHint} error={urlProblem || undefined}>
                    <div className="brd-store-urlrow">
                        <input className={`brd-input${urlError ? ' brd-input--invalid' : ''}`} aria-label="Product URL"
                            placeholder="https://yourbrand.com/products/..." value={form.product_url} disabled={disabled}
                            onChange={e => {
                                const val = e.target.value;
                                setForm(f => ({ ...f, product_url: val }));
                                setFetchStatus('idle');
                                if (urlError) setUrlError('');
                                if (val.trim() !== lastFetchedUrl) setLastFetchedUrl('');
                            }}
                            onBlur={() => {
                                const raw = (form.product_url || '').trim();
                                if (!raw) { setUrlError(''); return; }
                                const normalized = formValidation.NormalizeAffiliateLink(raw);
                                if (normalized !== form.product_url) {
                                    setForm(f => ({ ...f, product_url: normalized }));
                                }
                                const check = formValidation.GetAffiliateLinkValidate(normalized);
                                setUrlError(check.status ? '' : check.message);
                            }}
                            onKeyDown={e => {
                                if (e.key === 'Enter') { e.preventDefault(); fetchProductDetails(form.product_url); }
                            }} />
                        <button type="button" className="brd-btn brd-btn--secondary" onClick={() => fetchProductDetails(form.product_url)}
                            disabled={fetchingProduct || !form.product_url?.trim()}>
                            {fetchingProduct ? <span className="brd-spinner brd-spinner--sm" /> : <Sparkles size={16} />} Auto-fill
                        </button>
                    </div>
                </StoreField>
            </section>

            <section className="brd-section">
                <h3 className="brd-section__title">Details</h3>
                <StoreField label="Product title" required>
                    <input className="brd-input" aria-label="Product title" disabled={disabled} value={form.product_title}
                        onChange={e => setForm(f => ({ ...f, product_title: e.target.value }))} />
                </StoreField>
                <div className="brd-store-grid">
                    <StoreField label="SKU" hint="Auto-generated from the title if left blank.">
                        <input className="brd-input" aria-label="SKU" disabled={disabled} value={form.sku}
                            onChange={e => setForm(f => ({ ...f, sku: e.target.value }))} />
                    </StoreField>
                    <StoreField label="Category" hint="Shoppers can filter your store by it.">
                        <input className="brd-input" aria-label="Category" disabled={disabled} value={form.category} placeholder="e.g. Skincare"
                            onChange={e => setForm(f => ({ ...f, category: e.target.value }))} />
                    </StoreField>
                </div>
                <StoreField label="Description">
                    <textarea className="brd-textarea" aria-label="Description" disabled={disabled} value={form.product_description}
                        onChange={e => setForm(f => ({ ...f, product_description: e.target.value }))} />
                </StoreField>
            </section>

            <section className="brd-section">
                <h3 className="brd-section__title">Pricing &amp; commission</h3>
                <div className="brd-store-grid">
                    <StoreField label="Price" hint="Leave blank if no price is listed.">
                        <div className="brd-money">
                            <span className="brd-money__prefix">{form.currency}</span>
                            <input type="number" aria-label="Price" disabled={disabled} value={form.price} placeholder="0.00"
                                onChange={e => setForm(f => ({ ...f, price: e.target.value }))} />
                        </div>
                    </StoreField>
                    <StoreField label="Discount price" hint="Optional — must be lower than the price.">
                        <div className="brd-money">
                            <span className="brd-money__prefix">{form.currency}</span>
                            <input type="number" aria-label="Discount price" disabled={disabled} value={form.discount_price} placeholder="0.00"
                                onChange={e => setForm(f => ({ ...f, discount_price: e.target.value }))} />
                        </div>
                    </StoreField>
                </div>
                <div className="brd-store-grid">
                    <StoreField label="Currency">
                        <input className="brd-input" aria-label="Currency" disabled={disabled} value={form.currency}
                            onChange={e => setForm(f => ({ ...f, currency: e.target.value }))} />
                    </StoreField>
                    <StoreField label="Commission" hint="What creators earn on each sale.">
                        <div className="brd-money">
                            <input type="number" aria-label="Commission percent" value={form.commission_percent} placeholder="0"
                                onChange={e => setForm(f => ({ ...f, commission_percent: e.target.value }))} />
                            <span className="brd-money__prefix" style={{ marginRight: 0 }}>%</span>
                        </div>
                    </StoreField>
                </div>
                <div className="brd-store-switch-row">
                    <span>In stock</span>
                    <Toggle checked={form.in_stock} label="In stock" onChange={next => setForm(f => ({ ...f, in_stock: next }))} />
                </div>
                {cashkaroCampaignId && (
                    <div className="brd-store-switch-row">
                        <span>Feature in "{cashkaroCampaignTitle || 'Affiliate Deals'}"</span>
                        <Toggle checked={form.featured_in_campaign} label="Feature in Affiliate Deals"
                            onChange={next => setForm(f => ({ ...f, featured_in_campaign: next }))} />
                    </div>
                )}
            </section>

            <section className="brd-section">
                <h3 className="brd-section__title">Product images <span className="brd-field__req">*</span></h3>
                <p className="brd-field__hint">Up to 5 — the first is the primary image. Upload a file or paste a public URL.</p>
                {form.image_urls.map((url, idx) => (
                    <div key={idx} className="brd-store-imgrow">
                        {url
                            ? <img src={url} alt="" className="brd-store-table__thumb" />
                            : <span className="brd-store-table__thumb">{idx + 1}</span>}
                        <input className="brd-input" value={url} placeholder={idx === 0 ? 'Primary image URL' : `Image ${idx + 1} URL`}
                            aria-label={idx === 0 ? 'Primary image URL' : `Image ${idx + 1} URL`}
                            onChange={e => setImageUrl(idx, e.target.value)} />
                        <button type="button" className="brd-icon-btn" onClick={() => setUploadIdx(idx)} title="Upload image" aria-label="Upload image">
                            <Upload size={16} />
                        </button>
                        {idx > 0 && (
                            <button type="button" className="brd-icon-btn brd-store-table__archive" onClick={() => removeImageUrl(idx)} title="Remove" aria-label="Remove image">
                                <Trash2 size={16} />
                            </button>
                        )}
                    </div>
                ))}
                {form.image_urls.length < 5 && (
                    <button type="button" className="brd-link brd-store__regen" onClick={addImageUrl}><Plus size={14} /> Add image</button>
                )}
            </section>

            <div className="brd-store-form__footer brd-store-form__footer--inline">
                <div className="brd-store-form__footer-row">
                    <button type="button" className="brd-btn brd-btn--outline brd-btn--lg" onClick={onCancel} disabled={saving}>Cancel</button>
                    <button type="button" className="brd-btn brd-btn--primary brd-btn--lg" onClick={handleSubmit} disabled={saving} aria-busy={saving}>
                        {saving
                            ? <><span className="brd-spinner brd-spinner--sm brd-spinner--light" /> Saving…</>
                            : <><Check size={18} /> {product ? 'Save changes' : 'Add Product'}</>}
                    </button>
                </div>
            </div>

            {/* Image upload — fills the selected image slot with the uploaded file's public URL */}
            {uploadIdx !== null && (
                <FileUploadPopup
                    openModal={uploadIdx !== null}
                    onClose={() => setUploadIdx(null)}
                    acceptedFileTypes=".jpg, .jpeg, .png"
                    path={uploadPath}
                    coverImage={false}
                    onSend={(e: { url: string }) => {
                        if (uploadIdx !== null) setImageUrl(uploadIdx, e.url);
                        setUploadIdx(null);
                    }}
                />
            )}
        </div>
    );
};

const mapStateToProps = (state: any) => ({ UserReducer: state.UserReducer.UserData });
export const ProductForm = connect(mapStateToProps)(ProductFormComponent);
